<div class="min-h-0 overflow-hidden">
  <div
    class="flex flex-col gap-3 border-b border-neutral-grey-200 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
    <div>
      <h2 class="text-lg font-semibold text-neutral-grey-950">Mijn afspraken</h2>
      <p class="text-sm text-neutral-grey-700">Je planning voor deze maand</p>
    </div>

    <div class="flex flex-wrap items-center gap-6">
      <div class="flex flex-wrap items-center gap-3">

        <div class="flex items-center gap-1 rounded-full bg-neutral-grey-50 p-1">
          <button type="button"
                  class="btn-icon-only !px-2.5 !py-2 hover-effect-elevate rounded-full hover:text-secondary"
                  (click)="calendar().getApi().prev()">
            <span class="sr-only">Vorige maand</span>
            <ion-icon name="chevron-back-outline"
                      class="size-4"></ion-icon>
          </button>
          <time class="min-w-28 text-center text-sm font-semibold capitalize text-neutral-grey-950"
                [attr.datetime]="currentMonthLabel()">{{ currentMonthLabel() }}
          </time>
          <button type="button"
                  class="btn-icon-only !px-2.5 !py-2 hover-effect-elevate rounded-full hover:text-secondary"
                  (click)="calendar().getApi().next()">
            <span class="sr-only">Volgende maand</span>
            <ion-icon name="chevron-forward-outline"
                      class="size-4"></ion-icon>
          </button>
        </div>

        <button type="button"
                class="btn-hyperlink"
                (click)="calendar().getApi().today()">Vandaag
        </button>
      </div>

      @if (showCreateAction()) {
        <button class="btn-primary hover-effect-elevate gap-2"
                (click)="createClicked.emit()">
          <ion-icon name="add"
                    class="size-5"></ion-icon>
          Afspraak maken
        </button>
      }

    </div>
  </div>

  <div class="flex flex-col">
    <div class="overflow-hidden lg:flex lg:flex-auto lg:flex-col">
      <div class="grid grid-cols-7 gap-px text-center text-xs font-semibold text-gray-700 lg:flex-none">
        @for (day of weekdayHeaders(); track $index) {
          <div class="flex justify-center bg-white pt-6 pb-3 text-neutral-grey-950 text-sm font-medium">
            <span>{{ day | uppercase }}</span>
          </div>
        }
      </div>
      <div class="flex bg-gray-200 text-xs text-gray-700 lg:flex-auto">
        <div class="hidden w-full lg:grid lg:grid-cols-7 lg:grid-rows-6 lg:gap-px">
          @for (day of visibleDays(); track day.iso) {
            <div class="group relative bg-gray-50 px-3 py-2 text-gray-500 cursor-pointer lg:min-h-24"
                 (click)="selectDay(day.date, canEdit())"
                 [ngClass]="{'bg-white text-gray-900': !day.isOtherMonth, 'text-gray-400': day.isOtherMonth}">
              <time class="relative"
                    [ngClass]="{
                      'flex h-6 w-6 items-center justify-center rounded-full bg-primary font-semibold text-white': day.isToday,
                      'group-[&:not([data-current=true])]:opacity-75': day.isOtherMonth
                    }">
                {{ day.date.getDate() }}
              </time>
              @if (day.events.length) {
                <ol class="mt-2 space-y-1">
                  @for (ev of day.events; track ev.id) {
                    <li class="relative group">
                      <button type="button"
                              class="flex w-full text-left"
                              (click)="selectEvent(ev.id); $event.stopPropagation()">
                        <p
                          class="flex-auto truncate font-medium text-gray-900 group-hover:text-primary">{{ ev.title }}</p>
                        <span
                          class="ml-3 hidden flex-none text-gray-500 group-hover:text-primary xl:block">{{ ev.timeText }}</span>
                      </button>
                      <div
                        class="pointer-events-auto w-96 max-w-lg absolute left-0 top-full z-30 hidden lg:block opacity-0 translate-y-1 transition duration-150 ease-out lg:group-hover:opacity-100 lg:group-hover:translate-y-2 lg:group-focus-within:opacity-100 lg:group-focus-within:translate-y-2">
                        <ol
                          class="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white text-sm shadow-sm outline outline-1 outline-black/5">
                          <ng-container [ngTemplateOutlet]="eventCard"
                                        [ngTemplateOutletContext]="{ $implicit: ev }"></ng-container>
                        </ol>
                      </div>
                    </li>
                  }
                </ol>
              }
            </div>
          }
        </div>

        <div class="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden">
          @for (day of visibleDays(); track day.iso) {
            <button type="button"
                    class="group relative flex h-16 flex-col px-3 py-2 bg-white hover:bg-gray-100 focus:z-10"
                    [ngClass]="{
                      'bg-gray-50 text-gray-500': day.isOtherMonth,
                      'text-gray-900': !day.isOtherMonth,
                      'font-semibold text-primary': day.isToday
                    }"
                    (click)="selectDay(day.date, false)">
              <time class="ml-auto flex h-6 w-6 items-center justify-center rounded-full"
                    [ngClass]="{'bg-primary text-white': day.isToday}">
                {{ day.date.getDate() }}
              </time>
              <span class="sr-only">{{ day.events.length }} events</span>
              <span class="-mx-0.5 mt-auto flex flex-wrap-reverse">
                @if (day.events.length > 0) {
                  <span class="mx-0.5 mb-1 size-1.5 rounded-full bg-gray-400"></span>
                }
              </span>
            </button>
          }
        </div>
      </div>
    </div>

    <div class="relative px-4 py-10 sm:px-6 lg:hidden">
      <ol
        class="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white text-sm shadow-sm outline outline-1 outline-black/5">
        @if (selectedDayEvents().length) {
          @for (ev of selectedDayEvents(); track ev.id) {
            <ng-container [ngTemplateOutlet]="eventCard" [ngTemplateOutletContext]="{ $implicit: ev }"></ng-container>
          }
        } @else {
          <li class="flex items-center justify-center p-6 text-sm text-gray-600">Geen afspraken voor deze dag</li>
        }
      </ol>
    </div>

    <full-calendar class="sr-only absolute"
                   aria-hidden="true"
                   [options]="calendarOptions"></full-calendar>
  </div>
</div>

<ng-template #eventCard let-ev>
  <li class="group flex flex-col gap-3 p-4 pr-6" (click)="selectEvent(ev.id); $event.stopPropagation()">
    <div class="flex items-start gap-3">
      <span class="mt-1 size-3 rounded-full bg-primary"></span>
      <div class="flex-auto">
        <p class="font-semibold text-gray-900">{{ ev.title }}</p>
        <p class="text-sm text-neutral-grey-700">{{ ev.dateLong }}</p>
        <p class="text-sm text-neutral-grey-700">{{ ev.timeRange }}</p>
      </div>
    </div>
  </li>
</ng-template>
